import React from "react";
import styled from "styled-components";
import { Dialog, DialogContent, IconButton, Box, Typography, Switch } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type RectLike = { x: number; y: number; width: number; height: number; name?: string; color?: string };
type LineLike = { x1: number; y1: number; x2: number; y2: number };

const asNum = (v: any): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const normalizePlacement = (raw: any): RectLike | null => {
    if (!raw) return null;

    // Shape A: { x, y, width, height }
    const xA = asNum(raw.x);
    const yA = asNum(raw.y);
    const wA = asNum(raw.width);
    const hA = asNum(raw.height);
    if (xA !== null && yA !== null && wA !== null && hA !== null && wA > 0 && hA > 0) {
        return { x: xA, y: yA, width: wA, height: hA, name: raw.name, color: raw.color };
    }

    // Shape B: { position: {x,y}, size: {width,height} }
    const xB = asNum(raw.position?.x);
    const yB = asNum(raw.position?.y);
    const wB = asNum(raw.size?.width);
    const hB = asNum(raw.size?.height);
    if (xB !== null && yB !== null && wB !== null && hB !== null && wB > 0 && hB > 0) {
        return { x: xB, y: yB, width: wB, height: hB, name: raw.name, color: raw.color };
    }

    // Shape C: tuple [x,y,width,height,name?]
    if (Array.isArray(raw) && raw.length >= 4) {
        const xC = asNum(raw[0]);
        const yC = asNum(raw[1]);
        const wC = asNum(raw[2]);
        const hC = asNum(raw[3]);
        if (xC !== null && yC !== null && wC !== null && hC !== null && wC > 0 && hC > 0) {
            return { x: xC, y: yC, width: wC, height: hC, name: raw[4], color: undefined };
        }
    }

    return null;
};

const normalizeWall = (raw: any): LineLike | null => {
    if (!raw) return null;

    const x1A = asNum(raw.x1);
    const y1A = asNum(raw.y1);
    const x2A = asNum(raw.x2);
    const y2A = asNum(raw.y2);
    if (x1A !== null && y1A !== null && x2A !== null && y2A !== null) {
        return { x1: x1A, y1: y1A, x2: x2A, y2: y2A };
    }

    const x1B = asNum(raw.start?.x ?? raw.start?.[0]);
    const y1B = asNum(raw.start?.y ?? raw.start?.[1]);
    const x2B = asNum(raw.end?.x ?? raw.end?.[0]);
    const y2B = asNum(raw.end?.y ?? raw.end?.[1]);
    if (x1B !== null && y1B !== null && x2B !== null && y2B !== null) {
        return { x1: x1B, y1: y1B, x2: x2B, y2: y2B };
    }

    if (Array.isArray(raw) && raw.length >= 4) {
        const x1C = asNum(raw[0]);
        const y1C = asNum(raw[1]);
        const x2C = asNum(raw[2]);
        const y2C = asNum(raw[3]);
        if (x1C !== null && y1C !== null && x2C !== null && y2C !== null) {
            return { x1: x1C, y1: y1C, x2: x2C, y2: y2C };
        }
    }

    return null;
};

interface FloorplanPreviewModalProps {
    open: boolean;
    onClose: () => void;
    floorplans: any[];
    currentIndex: number;
    onIndexChange: (index: number) => void;
    onEditFloorplan: () => void;
    autoExpandEnabled?: boolean;
    onAutoExpandChange?: (enabled: boolean) => void;

    // Optional props for GA functionality
    onGenerateCirculation?: () => void;
    onGenerateAnotherFloorplan?: () => void;
    isGeneratingAnother?: boolean;
    isGALoading?: boolean;
    hasGAResult?: boolean;
    plotBoundary?: [number, number][];

    // Optional UI overrides
    title?: string;
    confirmButtonText?: string;
}

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px;
    min-width: 600px;
    min-height: 500px;
`;

const PreviewContainer = styled.div`
    flex: 1;
    width: 100%;
    background: #F5F5F5;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    min-height: 400px;
    position: relative;
`;

const NavigationButton = styled(IconButton)`
    position: absolute !important;
    background: rgba(251, 229, 198, 0.9) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;

    &:hover {
        background: #FBE5C6 !important;
    }
`;

const PaginationInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const AutoExpandRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 14px;
    border: 1px solid #1a1a1a;
    border-radius: 10px;
    background: #FBE5C6;
    margin: 0 auto 16px;
    width: 100%;
    max-width: 420px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 16px;
    align-items: center;
`;

const EditButton = styled.button`
    padding: 12px 32px;
    background: #FF9A56;
    color: #FBE5C6;
    border: none;
    border-radius: 8px;
    font-family: Poppins, sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #153a63;
    }

    &:disabled {
        background: #999;
        cursor: not-allowed;
    }
`;

const CirculationButton = styled.button<{ $done?: boolean }>`
    padding: 12px 32px;
    background: ${(props) => (props.$done ? "#4CAF50" : "#FF9A56")};
    color: ${(props) => (props.$done ? "#FBE5C6" : "#1a1a1a")};
    border: 2px solid #1a1a1a;
    border-radius: 8px;
    font-family: Poppins, sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 2px 2px 0px #1a1a1a;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover:not(:disabled) {
        transform: translate(-1px, -1px);
        box-shadow: 3px 3px 0px #1a1a1a;
    }

    &:active:not(:disabled) {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px 0px #1a1a1a;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const FloorplanPreviewModal: React.FC<FloorplanPreviewModalProps> = ({
    open,
    onClose,
    floorplans,
    currentIndex,
    onIndexChange,
    onEditFloorplan,
    autoExpandEnabled,
    onAutoExpandChange,
    onGenerateCirculation,
    onGenerateAnotherFloorplan,
    isGeneratingAnother = false,
    isGALoading = false,
    hasGAResult = false,
    plotBoundary = [],
    title,
    confirmButtonText = "Edit Floorplan",
}) => {
    void onGenerateCirculation;
    void isGALoading;
    void hasGAResult;

    const canToggleAutoExpand = typeof autoExpandEnabled === "boolean" && typeof onAutoExpandChange === "function";

    const handlePrev = () => {
        if (currentIndex > 0) {
            onIndexChange(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (currentIndex < floorplans.length - 1) {
            onIndexChange(currentIndex + 1);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            sx={{ zIndex: 99999 }}
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent>
                <ModalContent>
                    <Typography variant="h5" sx={{ mb: 3, fontFamily: "Poppins", fontWeight: 600 }}>
                        {title || (hasGAResult ? "Circulation Output Preview" : "Floorplan Preview")}
                    </Typography>

                    <PreviewContainer>
                        <NavigationButton
                            onClick={handlePrev}
                            disabled={currentIndex === 0}
                            sx={{ left: 16 }}
                        >
                            <ChevronLeftIcon />
                        </NavigationButton>

                        <Box sx={{ flex: 1, height: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
                            {floorplans.length > 0 && floorplans[currentIndex] ? (
                                (() => {
                                    const fp = floorplans[currentIndex];
                                    const rawWalls = fp.walls || [];
                                    const rawPlacements = fp.placements || [];
                                    const rawRegions = fp.floor?.regions || [];

                                    const walls = rawWalls.map(normalizeWall).filter(Boolean) as LineLike[];
                                    let placements = rawPlacements.map(normalizePlacement).filter(Boolean) as RectLike[];

                                    // Fallback for APIs that return only floor regions
                                    if (placements.length === 0 && rawRegions.length > 0) {
                                        placements = rawRegions
                                            .map((r: any, idx: number) => {
                                                const x = asNum(r.x);
                                                const y = asNum(r.y);
                                                const width = asNum(r.width);
                                                const height = asNum(r.height);
                                                if (x === null || y === null || width === null || height === null || width <= 0 || height <= 0) return null;
                                                return { x, y, width, height, name: r.name || `Region ${idx + 1}` } as RectLike;
                                            })
                                            .filter(Boolean) as RectLike[];
                                    }

                                    const roomWallGroups = (fp.rooms || []).map((r: any) => ({
                                        name: r?.name,
                                        walls: (r?.walls || []).map(normalizeWall).filter(Boolean) as LineLike[],
                                    })).filter((g: any) => g.walls.length > 0);

                                    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                                    const roomPalette = ["#E7F0FF", "#FFE9D8", "#EAF9EE", "#FFF4C7", "#F3E9FF", "#FFE5EC", "#DCF7F5"];

                                    // Bounds from generated floorplan content
                                    let contentMinX = Infinity, contentMinY = Infinity, contentMaxX = -Infinity, contentMaxY = -Infinity;

                                    if (walls.length > 0) {
                                        walls.forEach((w) => {
                                            contentMinX = Math.min(contentMinX, w.x1, w.x2);
                                            contentMinY = Math.min(contentMinY, w.y1, w.y2);
                                            contentMaxX = Math.max(contentMaxX, w.x1, w.x2);
                                            contentMaxY = Math.max(contentMaxY, w.y1, w.y2);
                                        });
                                    }

                                    if (placements.length > 0) {
                                        placements.forEach((p) => {
                                            contentMinX = Math.min(contentMinX, p.x);
                                            contentMinY = Math.min(contentMinY, p.y);
                                            contentMaxX = Math.max(contentMaxX, p.x + p.width);
                                            contentMaxY = Math.max(contentMaxY, p.y + p.height);
                                        });
                                    }

                                    if (roomWallGroups.length > 0) {
                                        roomWallGroups.forEach((r: any) => {
                                            r.walls.forEach((w: any) => {
                                                contentMinX = Math.min(contentMinX, w.x1, w.x2);
                                                contentMinY = Math.min(contentMinY, w.y1, w.y2);
                                                contentMaxX = Math.max(contentMaxX, w.x1, w.x2);
                                                contentMaxY = Math.max(contentMaxY, w.y1, w.y2);
                                            });
                                        });
                                    }

                                    const regionBoundarySegments: LineLike[] = (rawRegions || []).flatMap((region: any) => {
                                        const x = asNum(region?.x);
                                        const y = asNum(region?.y);
                                        const width = asNum(region?.width);
                                        const height = asNum(region?.height);
                                        if (x === null || y === null || width === null || height === null || width <= 0 || height <= 0) {
                                            return [];
                                        }
                                        return [
                                            { x1: x, y1: y, x2: x + width, y2: y },
                                            { x1: x + width, y1: y, x2: x + width, y2: y + height },
                                            { x1: x + width, y1: y + height, x2: x, y2: y + height },
                                            { x1: x, y1: y + height, x2: x, y2: y },
                                        ];
                                    });

                                    const requestBoundarySegments: LineLike[] =
                                        Array.isArray(plotBoundary) && plotBoundary.length > 2
                                            ? plotBoundary.map((point, idx) => {
                                                const [x1, y1] = point;
                                                const [x2, y2] = plotBoundary[(idx + 1) % plotBoundary.length];
                                                return { x1, y1, x2, y2 };
                                            })
                                            : [];

                                    const displayBoundarySegments =
                                        requestBoundarySegments.length > 0
                                            ? requestBoundarySegments
                                            : regionBoundarySegments;

                                    if (displayBoundarySegments.length > 0) {
                                        displayBoundarySegments.forEach((segment) => {
                                            minX = Math.min(minX, segment.x1, segment.x2);
                                            minY = Math.min(minY, segment.y1, segment.y2);
                                            maxX = Math.max(maxX, segment.x1, segment.x2);
                                            maxY = Math.max(maxY, segment.y1, segment.y2);
                                        });
                                    } else if (plotBoundary && plotBoundary.length > 0) {
                                        plotBoundary.forEach(([x, y]) => {
                                            minX = Math.min(minX, x);
                                            minY = Math.min(minY, y);
                                            maxX = Math.max(maxX, x);
                                            maxY = Math.max(maxY, y);
                                        });
                                    }

                                    if (walls.length > 0) {
                                        walls.forEach((w) => {
                                            minX = Math.min(minX, w.x1, w.x2);
                                            minY = Math.min(minY, w.y1, w.y2);
                                            maxX = Math.max(maxX, w.x1, w.x2);
                                            maxY = Math.max(maxY, w.y1, w.y2);
                                        });
                                    } else if (placements.length > 0) {
                                        placements.forEach((p) => {
                                            minX = Math.min(minX, p.x);
                                            minY = Math.min(minY, p.y);
                                            maxX = Math.max(maxX, p.x + p.width);
                                            maxY = Math.max(maxY, p.y + p.height);
                                        });
                                    } else if (roomWallGroups.length > 0) {
                                        roomWallGroups.forEach((r: any) => {
                                            r.walls.forEach((w: any) => {
                                                minX = Math.min(minX, w.x1, w.x2);
                                                minY = Math.min(minY, w.y1, w.y2);
                                                maxX = Math.max(maxX, w.x1, w.x2);
                                                maxY = Math.max(maxY, w.y1, w.y2);
                                            });
                                        });
                                    }

                                    if (minX === Infinity) return <Typography>Invalid Data</Typography>;

                                    const width = maxX - minX;
                                    const height = maxY - minY;
                                    const padding = Math.max(width, height) * 0.1;
                                    const viewBox = `${minX - padding} ${minY - padding} ${width + padding * 2} ${height + padding * 2}`;
                                    const baseStroke = Math.max(0.35, Math.min(width, height) / 180);

                                    return (
                                        <svg
                                            viewBox={viewBox}
                                            preserveAspectRatio="xMidYMid meet"
                                            style={{ width: "100%", height: "100%", maxHeight: "360px" }}
                                        >
                                            <rect
                                                x={minX - padding}
                                                y={minY - padding}
                                                width={width + padding * 2}
                                                height={height + padding * 2}
                                                fill="#F9FAFB"
                                            />
                                            {displayBoundarySegments.length > 0 && displayBoundarySegments.map((segment, idx) => (
                                                <line
                                                    key={`plot-${idx}`}
                                                    x1={segment.x1}
                                                    y1={segment.y1}
                                                    x2={segment.x2}
                                                    y2={segment.y2}
                                                    stroke="#6D7278"
                                                    strokeWidth={baseStroke}
                                                    strokeDasharray={`${baseStroke * 4} ${baseStroke * 2}`}
                                                />
                                            ))}
                                            {regionBoundarySegments.length === 0 && plotBoundary && plotBoundary.length > 2 && (
                                                <polygon
                                                    points={plotBoundary.map(([x, y]) => `${x},${y}`).join(" ")}
                                                    fill="rgba(60, 92, 130, 0.08)"
                                                    stroke="#3C5C82"
                                                    strokeWidth={baseStroke}
                                                    strokeLinejoin="round"
                                                />
                                            )}
                                            {/* Render walls if available */}
                                            {walls.length > 0 && walls.map((w, i: number) => (
                                                <line
                                                    key={i}
                                                    x1={w.x1} y1={w.y1}
                                                    x2={w.x2} y2={w.y2}
                                                    stroke="#2A2A2A"
                                                    strokeWidth={baseStroke}
                                                />
                                            ))}
                                            {/* Render placements (rooms) if available */}
                                            {placements.length > 0 && placements.map((p, i: number) => (
                                                <g key={i}>
                                                    <rect
                                                        x={p.x} y={p.y}
                                                        width={p.width} height={p.height}
                                                        fill={p.color || roomPalette[i % roomPalette.length]}
                                                        stroke="#2A2A2A"
                                                        strokeWidth={baseStroke * 0.8}
                                                        opacity={0.85}
                                                        rx={0.3}
                                                    />
                                                    <text
                                                        x={p.x + p.width / 2}
                                                        y={p.y + p.height / 2}
                                                        dominantBaseline="central"
                                                        textAnchor="middle"
                                                        fontSize={Math.max(0.6, Math.min(2.5, Math.min(p.width, p.height) * 0.15))}
                                                        fill="#1D1D1D"
                                                        style={{
                                                            fontWeight: 600,
                                                            textShadow: "0px 0px 4px rgba(251, 229, 198, 0.8)"
                                                        }}
                                                    >
                                                        {p.name || `Room ${i + 1}`}
                                                    </text>
                                                </g>
                                            ))}
                                            {/* Render explicit rooms (GA output) */}
                                            {roomWallGroups.map((r: any, i: number) => (
                                                <g key={i}>
                                                    {r.walls.map((w: any, j: number) => (
                                                        <line
                                                            key={j}
                                                            x1={w.x1} y1={w.y1}
                                                            x2={w.x2} y2={w.y2}
                                                            stroke="#2A2A2A"
                                                            strokeWidth={baseStroke}
                                                        />
                                                    ))}
                                                    <text
                                                        x={(() => {
                                                            const xs = r.walls.flatMap((w: any) => [w.x1, w.x2]);
                                                            return xs.length ? (Math.min(...xs) + Math.max(...xs)) / 2 : 0;
                                                        })()}
                                                        y={(() => {
                                                            const ys = r.walls.flatMap((w: any) => [w.y1, w.y2]);
                                                            return ys.length ? (Math.min(...ys) + Math.max(...ys)) / 2 : 0;
                                                        })()}
                                                        dominantBaseline="central"
                                                        textAnchor="middle"
                                                        fontSize={(() => {
                                                            const xs = r.walls.flatMap((w: any) => [w.x1, w.x2]);
                                                            const ys = r.walls.flatMap((w: any) => [w.y1, w.y2]);
                                                            const rw = xs.length ? Math.max(...xs) - Math.min(...xs) : width;
                                                            const rh = ys.length ? Math.max(...ys) - Math.min(...ys) : height;
                                                            return Math.max(baseStroke * 3, Math.min(baseStroke * 8, Math.min(rw, rh) * 0.15));
                                                        })()}
                                                        fill="#1D1D1D"
                                                        style={{
                                                            fontWeight: 600,
                                                            textShadow: "0px 0px 4px rgba(251, 229, 198, 0.8)"
                                                        }}
                                                    >
                                                        {r.name || `Room ${i + 1}`}
                                                    </text>
                                                </g>
                                            ))}
                                        </svg>
                                    );
                                })()
                            ) : (
                                <Typography sx={{ color: "#999" }}>
                                    No floorplans available
                                </Typography>
                            )}
                        </Box>

                        <NavigationButton
                            onClick={handleNext}
                            disabled={currentIndex >= floorplans.length - 1}
                            sx={{ right: 16 }}
                        >
                            <ChevronRightIcon />
                        </NavigationButton>
                    </PreviewContainer>

                    <PaginationInfo>
                        <Typography sx={{ fontFamily: "Poppins", color: "#666" }}>
                            {floorplans.length > 0 ? `${currentIndex + 1} / ${floorplans.length}` : "0 / 0"}
                        </Typography>
                    </PaginationInfo>

                    {canToggleAutoExpand && (
                        <AutoExpandRow>
                            <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, color: "#1a1a1a" }}>
                                    Auto-Expand Rooms
                                </Typography>
                                <Typography sx={{ fontFamily: "Poppins", fontSize: 12, color: "#6d6d6d" }}>
                                    Applies to Generate Another Floorplan
                                </Typography>
                            </Box>
                            <Switch
                                checked={autoExpandEnabled}
                                onChange={(event) => onAutoExpandChange(event.target.checked)}
                                inputProps={{ "aria-label": "auto expand rooms" }}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "#FF9A56",
                                        "&:hover": {
                                            backgroundColor: "rgba(255, 154, 86, 0.08)",
                                        },
                                    },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                        backgroundColor: "#FF9A56",
                                    },
                                }}
                            />
                        </AutoExpandRow>
                    )}

                    <ButtonGroup>
                        {onGenerateAnotherFloorplan && (
                            <CirculationButton
                                onClick={onGenerateAnotherFloorplan}
                                disabled={isGeneratingAnother || floorplans.length === 0}
                            >
                                {isGeneratingAnother ? (
                                    "Generating..."
                                ) : (
                                    "Generate Another Floorplan"
                                )}
                            </CirculationButton>
                        )}

                        <EditButton onClick={onEditFloorplan} disabled={floorplans.length === 0}>
                            {confirmButtonText}
                        </EditButton>
                    </ButtonGroup>
                </ModalContent>
            </DialogContent>
        </Dialog>
    );
};

export default FloorplanPreviewModal;
